import { t } from '@lingui/macro'
import { CreateMultiLegShippingEstimateByFuelResponse } from '@lune-climate/lune'
import { LuneTheme, Text } from '@lune-fe/lune-ui-lib'
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined'
import { Box } from '@mui/material'
import React, { FC } from 'react'

import { getFormattedAmount } from '../../utils'

import { LegsScrollableContainer } from './Legs'

const { palette } = LuneTheme

type Leg = CreateMultiLegShippingEstimateByFuelResponse['legs'][number]

const MultiLegShippingByFuelExplanationLegBox: FC<{
    leg: Leg
    legNumber: number
    active: boolean
    onClick: (legNumber: number, legElement: HTMLButtonElement) => void
    smallBoxes: boolean
}> = ({ leg, legNumber, active, onClick, smallBoxes }) => {
    const legFormattedAmount = (leg: Leg): string => {
        const formattedAmount = getFormattedAmount(leg.mass.amount)
        return `${formattedAmount.displayAmount} ${formattedAmount.amountUnit}CO₂e`
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick(legNumber, event.currentTarget)
    }

    return (
        <Box
            data-testid={`leg-btn-${legNumber}`}
            onClick={handleClick}
            component={'button'}
            sx={{
                background: active ? palette.Blue50 : palette.Grey100,
                mt: 1,
                mb: 1,
                height: '160px',
                borderRadius: '16px',
                p: 2,
                minWidth: '120px',
                maxWidth: active ? '320px' : smallBoxes ? '122px' : '160px',
                width: active ? '320px' : '100%',
                flex: '1 0 auto',
                transition: 'background .15s ease-in-out',
                overflow: 'hidden',
                cursor: 'pointer',
                border: 'none',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'space-between',
                justifyContent: 'space-between',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    padding: '10px 14px 10px 12px',
                    borderRadius: '40px',
                    background: active ? palette.Blue500 : palette.Grey300,
                    transition: 'background .15s ease-in-out',
                    color: active ? palette.White : palette.Grey900,
                    svg: {
                        height: '20px',
                        width: '20px',
                    },
                    span: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    },
                }}
            >
                <Text variant={'button'}>
                    <LocalGasStationOutlinedIcon /> {t`Leg ${legNumber}`}
                </Text>
            </Box>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Text variant={'body2'}>{leg.emissionFactor.name}</Text>
                <Text variant={'caption'} data-testid={`leg-emissions-amount-${legNumber}`}>
                    {legFormattedAmount(leg)}
                </Text>
            </Box>
        </Box>
    )
}

export const MultiLegShippingByFuelExplanationLegs = ({
    estimate,
    setActiveTab,
    activeTabIndex,
}: {
    estimate: CreateMultiLegShippingEstimateByFuelResponse
    setActiveTab: (tabIndex: number) => void
    activeTabIndex: number | null
}) => {
    const { legs } = estimate

    const handleLegClick = (legNumber: number, legElement: HTMLButtonElement) => {
        setActiveTab(legNumber)

        legElement.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        })
    }

    return (
        <LegsScrollableContainer>
            <Box
                sx={{
                    pr: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        gap: 1,
                        flexWrap: 'nowrap',
                        width: 'max-content',
                    }}
                >
                    {legs.map((leg, index) => {
                        return (
                            <MultiLegShippingByFuelExplanationLegBox
                                key={index}
                                leg={leg}
                                smallBoxes={legs.length > 5}
                                legNumber={index + 1}
                                active={activeTabIndex === index + 1}
                                onClick={handleLegClick}
                            />
                        )
                    })}
                </Box>
            </Box>
        </LegsScrollableContainer>
    )
}

export default MultiLegShippingByFuelExplanationLegs
